import React, {useState, useEffect, useRef} from 'react';
import {socket} from "./socket";
import {useServiceWorker} from './useServiceWorker';
import GameMasterInterface from "./components/GamemasterInterface";
import PlayerInterface from "./components/PlayerInterface";

// Fonctions utilitaires pour la persistance locale
const saveLocalState = (state) => {
    localStorage.setItem('werewolf_game_state', JSON.stringify({
        ...state,
        timestamp: Date.now()
    }));
};

const loadLocalState = () => {
    try {
        const saved = localStorage.getItem('werewolf_game_state');
        if (saved) {
            const data = JSON.parse(saved);
            const timeDiff = Date.now() - data.timestamp;

            // Ne restaurer que si récent (< 5 minutes)
            if (timeDiff < 300000) {
                return data;
            }
        }
    } catch (error) {
        console.error('Failed to load local state:', error);
    }
    return null;
};

async function requestWakeLock() {
    try {
        if ('wakeLock' in navigator) {
            const lock = await navigator.wakeLock.request('screen');
            console.log('Wake Lock activé');
            return lock;
        }
    } catch (err) {
        console.error('Wake Lock request failed:', err);
    }
    return null;
}

async function requestNotificationPermission() {
    if ('Notification' in window) {
        await Notification.requestPermission();
    }
}

function PlayerApp() {
    const [wakeLock, setWakeLock] = useState(null);
    const {registration, error: swError, isReady} = useServiceWorker();

    useEffect(() => {
        requestNotificationPermission();

        socket.connect();
        console.log('Connected');
        // Demander le Wake Lock au démarrage
        requestWakeLock().then(lock => setWakeLock(lock));

        // Gérer la réactivation quand l'utilisateur revient sur l'app
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                const lock = await requestWakeLock();
                setWakeLock(lock);
                // Vérifier la connexion socket
                if (!socket.connected) {
                    console.log('Page became visible, checking connection');
                    socket.connect();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            socket.disconnect();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            // Relâcher le Wake Lock si il existe
            if (wakeLock) wakeLock.release();
        };
    }, []);

    useEffect(() => {
        // Configuration de la géolocalisation périodique
        let watchId = null;
        let backgroundTask = null;

        async function setupLocationTracking() {
            // Demander la permission pour la géolocalisation en arrière-plan
            const permission = await navigator.permissions.query({
                name: 'geolocation'
            });

            // Configuration de la géolocalisation périodique (commenté comme dans l'original)
            // if (permission.state === 'granted') {
            //     watchId = navigator.geolocation.watchPosition(
            //         handlePosition,
            //         handleError,
            //         {
            //             enableHighAccuracy: true,
            //             timeout: 10000,
            //             maximumAge: 0
            //         }
            //     );
            //
            //     backgroundTask = setInterval(() => {
            //         navigator.geolocation.getCurrentPosition(
            //             handlePosition,
            //             handleError,
            //             {
            //                 enableHighAccuracy: true,
            //                 timeout: 10000,
            //                 maximumAge: 0
            //             }
            //         );
            //     }, 15000); // 15 secondes
            // }
        }

        function handlePosition(position) {
            const locationData = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: new Date().toISOString()
            };

            // Envoyer au serveur via socket
            if (socket) {
                socket.emit('update-position', locationData);
            }

            // Informer le service worker
            if (registration && registration.active) {
                registration.active.postMessage({
                    type: 'LOCATION_UPDATE',
                    position: locationData
                });
            }
        }

        function handleError(error) {
            console.error('Erreur de géolocalisation:', error);
        }

        // Démarrer le suivi
        setupLocationTracking();

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            // Message personnalisé (ne fonctionne pas sur tous les navigateurs)
            const message = "Attention ! Si vous quittez la page, vous serez déconnecté de la partie.";
            e.returnValue = message; // Standard
            return message; // Pour les anciens navigateurs
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Nettoyage
        return () => {
            if (watchId) navigator.geolocation.clearWatch(watchId);
            if (backgroundTask) clearInterval(backgroundTask);
            if (socket) socket.disconnect();
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    return (
        <App/>
    );
}

function App() {
    const [role, setRole] = useState(null);
    const [name, setName] = useState("");
    const [inputPassword, setInputPassword] = useState('');
    const [error, setError] = useState('');
    const [isGameStarted, setIsGameStarted] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [victory, setVictory] = useState("");
    const [showModal, setShowModal] = useState(false);

    // États pour la gestion de reconnexion
    const [isConnected, setIsConnected] = useState(socket.connected);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const [gameState, setGameState] = useState(null);
    const reconnectTimeoutRef = useRef(null);

    // Fonction de tentative de reconnexion
    const attemptReconnection = () => {
        if (reconnectAttempts < 5) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);

            reconnectTimeoutRef.current = setTimeout(() => {
                console.log(`Reconnection attempt ${reconnectAttempts + 1}`);
                setReconnectAttempts(prev => prev + 1);
                socket.connect();
            }, delay);
        }
    };

    // Composant de statut de connexion
    const ConnectionStatus = () => {
        if (isConnected) return null;

        return (
            <div className="connection-status offline">
                <div className="status-message">
                    Connexion perdue...
                    {reconnectAttempts > 0 && (
                        <span>Tentative {reconnectAttempts}/5</span>
                    )}
                </div>
                <button onClick={() => {
                    setReconnectAttempts(0);
                    socket.connect();
                }}>
                    Reconnecter manuellement
                </button>
            </div>
        );
    };

    const Version = () => {
        return <p>V1.3</p>
    }

    // Initialisation du socket avec gestion de reconnexion
    useEffect(() => {
        // Gestionnaires de connexion
        socket.on('connect', () => {
            console.log('Connected to server');
            setIsConnected(true);
            setReconnectAttempts(0);
            setIsGameStarted(false);
            setRole(null);
            setInputPassword('');

            // Tenter de restaurer la session
            const localState = loadLocalState();
            if (localState && localState.playerName) {
                socket.emit('player-reconnect', {
                    playerId: socket.id,
                    playerName: localState.playerName
                });
            }
        });

        socket.on('disconnect', (reason) => {
            console.log('Disconnected:', reason);
            setIsConnected(false);

            // Tentative de reconnexion automatique
            if (reason !== 'io client disconnect') {
                attemptReconnection();
            }
        });

        socket.on('game-state-restore', (data) => {
            console.log('Game state restored:', data);
            setGameState(data);
            // Restaurer l'interface utilisateur avec les données reçues
        });

        socket.on('reconnection-failed', () => {
            console.log('Reconnection failed - starting new session');
            localStorage.removeItem('werewolf_game_state');
        });

        socket.on('game-ended-disconnection', (data) => {
            alert(`Partie terminée: ${data.reason}`);
            localStorage.removeItem('werewolf_game_state');
        });

        socket.on('auth_response', (response) => {
            setIsConnecting(false);
            if (response.success) {
                setIsGameStarted(true);
                // Sauvegarder l'état local
                saveLocalState({
                    role,
                    playerName: name,
                    isGameStarted: true
                });
            } else {
                setIsGameStarted(false);
                setRole(null);
                setError(response.message || 'Authentification échouée');
            }
        });

        socket.on('game_ended', (response) => {
            console.log(response);
            setIsGameStarted(false);
            setRole(null);
            setInputPassword('');
            localStorage.removeItem('werewolf_game_state');

            if (response && response.loup) {
                setVictory("Les loups ont gagné !");
                setShowModal(true);
            } else if (response.villageois) {
                setVictory("Les villageois ont gagné !");
                setShowModal(true);
            } else if(response.timeIsUp) {
                setVictory("Le temps est écoulé !");
                setShowModal(true);
            }
        });

        return () => {
            socket.off('connect');
            socket.off('disconnect');
            socket.off('game-state-restore');
            socket.off('reconnection-failed');
            socket.off('game-ended-disconnection');
            socket.off('game_ended');
            socket.off('auth_response');
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            socket.disconnect();
        };
    }, []);

    const handleRoleSelection = (selectedRole) => {
        if (selectedRole === 'player') {
            setRole('player');
            const playerName = prompt('Entrez votre nom');
            if (!playerName || playerName.length === 0) {
                setError('Veuillez entrer un nom');
                setIsGameStarted(false);
                setRole(null);
                return;
            }
            setName(playerName);

            // Sauvegarder l'état local
            saveLocalState({
                role: 'player',
                playerName: playerName
            });

            socket.emit('join_as_player', {
                name: playerName,
                position: {
                    lat: 48.8566,
                    lng: 2.3522
                }
            });
            setIsGameStarted(true);
        } else {
            setRole('gamemaster');
        }
    };

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        setIsConnecting(true);
        setError('');

        if (inputPassword.length < 4) {
            setError('Le mot de passe doit contenir au moins 4 caractères');
            setIsConnecting(false);
            return;
        }

        // Envoi de la demande d'authentification au serveur
        socket.emit('authenticate_gamemaster', {
            password: inputPassword,
            isNewGame: role === 'gamemaster'
        });
    };

    if (isGameStarted) {
        return (
            <div>
                <ConnectionStatus />
                {role === 'gamemaster' && (
                    <>
                        <GameMasterInterface/>
                    </>
                )}
                {role === 'player' && (
                    <>
                        <PlayerInterface/>
                    </>
                )}
                <Version/>
            </div>
        );
    }

    return (
        <>
            <ConnectionStatus />
            {showModal ? (
                <>
                    <div
                        className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none"
                    >
                        <div className="relative w-auto my-6 mx-auto max-w-3xl">
                            {/*content*/}
                            <div
                                className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                                {/*header*/}
                                <div
                                    className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
                                    <h3 className="text-3xl font-semibold">
                                        Votre rôle
                                    </h3>
                                </div>
                                {/*body*/}
                                <div className="relative p-6 flex-auto">
                                    <p className="my-4 text-blueGray-500 text-lg leading-relaxed">
                                        {victory}
                                    </p>
                                </div>
                                {/*footer*/}
                                <div
                                    className="flex items-center justify-center p-6 border-t border-solid border-blueGray-200 rounded-b">
                                    <button
                                        className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                                        type="button"
                                        onClick={() => setShowModal(false)}
                                    >
                                        C'est noté !
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
                </>
            ) : null}
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="max-w-md w-full bg-white rounded-xl shadow-md p-6">
                    {!role ? (
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-center">
                                Choisissez votre rôle
                            </h2>
                            <div className="space-y-4">
                                <button
                                    onClick={() => handleRoleSelection('player')}
                                    className="w-full py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                                    disabled={isConnecting}
                                >
                                    Rejoindre en tant que Joueur
                                </button>
                                <button
                                    onClick={() => handleRoleSelection('gamemaster')}
                                    className="w-full py-3 px-4 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition"
                                    disabled={isConnecting}
                                >
                                    Devenir Game Master
                                </button>
                            </div>
                            {error && (
                                <p className="text-red-500 text-sm mt-2">{error}</p>
                            )}
                        </div>
                    ) : (
                        <form onSubmit={handlePasswordSubmit} className="space-y-4">
                            <h2 className="text-2xl font-bold mb-6 text-center">
                                {role === 'gamemaster'
                                    ? 'Créez un mot de passe'
                                    : 'Entrez le mot de passe Game Master'}
                            </h2>
                            <div>
                                <input
                                    type="password"
                                    value={inputPassword}
                                    onChange={(e) => setInputPassword(e.target.value)}
                                    placeholder="Mot de passe"
                                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    disabled={isConnecting}
                                    autoFocus
                                />
                                {error && (
                                    <p className="text-red-500 text-sm mt-2">{error}</p>
                                )}
                            </div>
                            <button
                                type="submit"
                                className="w-full py-3 px-4 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition"
                                disabled={isConnecting}
                            >
                                {isConnecting ? (
                                    <span>Connexion en cours...</span>
                                ) : (
                                    <span>{role === 'gamemaster' ? 'Créer la partie' : 'Connexion'}</span>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setRole(null)}
                                className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition"
                                disabled={isConnecting}
                            >
                                Retour
                            </button>
                        </form>
                    )}
                </div>
            </div>
            <Version/>
            {/* CSS pour le statut de connexion */}
            <style jsx>{`
                .connection-status {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                    padding: 10px;
                    text-align: center;
                    font-weight: bold;
                }

                .connection-status.offline {
                    background-color: #ff4444;
                    color: white;
                }

                .connection-status.reconnecting {
                    background-color: #ffaa00;
                    color: white;
                }

                .status-message {
                    margin-bottom: 5px;
                }

                .connection-status button {
                    background: rgba(255,255,255,0.2);
                    border: 1px solid white;
                    color: white;
                    padding: 5px 10px;
                    border-radius: 3px;
                    cursor: pointer;
                }
            `}</style>

        </>
    );
}

export default PlayerApp;